import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DeleteModal from 'src/components/DeleteModal';
import MapBox from 'src/components/MapBox/MapBox';
import ShipperConsigneeAutocomplete from 'src/components/ShipperConsigneeAutocomplete/ShipperConsigneeAutocomplete';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { setConsigneeOptions, setConsignees, setShipperOptions, setShippers } from 'src/redux/address.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { deleteSingleAddress } from 'src/services/AddressBookServices';
import WalToast from 'src/utils/WalToast';
import * as Yup from 'yup';

import { QUOTE_KEYS } from '../createQuote.const';
import { shipperConsigneeDetails as shipperConsigneeDetailsSelector, setCreateQuoteData } from '../CreateQuote.slice';

import ShipperConsigneeForm from './ShipperConsigneeDetails/ShipperConsigneeForm';
import StepperAction from './StepperAction';
import { useSaveNextStep } from './useSaveNextStep';

export const INITIAL_DELETE_ACTION = {
    mode: '',
    shipper: false,
    consignee: false,
    isContact: false,
    contactId: null,
};

const ShipperConsignee = ({ isShipper = true, setHeaderChildren }: { isShipper?: boolean; setHeaderChildren: (children: React.ReactNode) => void }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const shipperConsigneeDetails = useAppSelector(shipperConsigneeDetailsSelector);
    const { shipperOptions = [], consigneeOptions = [], shippers = [], consignees = [], handleSearchShipper, handleSearchConsignee } = useAddressDetails();
    const { onSaveNext, onBack } = useSaveNextStep();
    const [isEditId, setIsEditId] = useState(false);
    const [isEditConsigneeId, setIsEditConsigneeId] = useState(false);
    const [deleteAction, setDeleteAction] = useState(INITIAL_DELETE_ACTION);
    const [isDeleting, setIsDeleting] = useState(false);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                shipperId: Yup.number().nullable(),
                shipperCompanyName: Yup.string().when('shipperId', {
                    is: (shipperId: any) =>
                        // If shipperId exists (saved shipper/consignee), company name is required
                        // If only address exists without shipperId, company name is optional
                        shipperId !== null && shipperId !== undefined,
                    then: (schema) => schema.required('Shipper company name is required'),
                    otherwise: (schema) => schema.nullable(),
                }),
                shipperAddress: Yup.object()
                    .shape({
                        fullAddress: Yup.string().required(t('ValidationMessages.Shipper_address_is_required')),
                        latitude: Yup.string().required(t('ValidationMessages.Shipper_latitude_is_required')),
                        longitude: Yup.string().required(t('ValidationMessages.Shipper_longitude_is_required')),
                        city: Yup.string().required('City is required'),
                        state: Yup.string().required('State is required'),
                        country: Yup.string().required('Country is required'),
                    })
                    .nullable()
                    .required('Shipper address is required'),
                consigneeId: Yup.number().nullable(),
                consigneeCompanyName: Yup.string().when('consigneeId', {
                    is: (consigneeId: any) =>
                        // If consigneeId exists (saved consignee), company name is required
                        // If only address exists without consigneeId, company name is optional
                        consigneeId !== null && consigneeId !== undefined,
                    then: (schema) => schema.required('Consignee company name is required'),
                    otherwise: (schema) => schema.nullable(),
                }),
                consigneeAddress: Yup.object()
                    .shape({
                        fullAddress: Yup.string().required(t('ValidationMessages.Consignee_address_is_required')),
                        latitude: Yup.string().required(t('ValidationMessages.Consignee_latitude_is_required')),
                        longitude: Yup.string().required(t('ValidationMessages.Consignee_longitude_is_required')),
                        city: Yup.string().required('City is required'),
                        state: Yup.string().required('State is required'),
                        country: Yup.string().required('Country is required'),
                    })
                    .nullable()
                    .required('Consignee address is required'),
            }),
        [t]
    );

    const defaultValues = useMemo(
        () => ({
            shipperId: shipperConsigneeDetails?.shipperId || null,
            shipperCompanyName: shipperConsigneeDetails?.shipperCompanyName || '',
            shipperAddress: shipperConsigneeDetails?.shipperAddress || null,
            consigneeId: shipperConsigneeDetails?.consigneeId || null,
            consigneeCompanyName: shipperConsigneeDetails?.consigneeCompanyName || '',
            consigneeAddress: shipperConsigneeDetails?.consigneeAddress || null,
        }),
        [shipperConsigneeDetails]
    );

    const methods = useForm<any>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onChange',
        defaultValues,
    });

    const {
        control,
        watch,
        formState: { errors, isSubmitted },
        setValue,
        handleSubmit,
        trigger,
        getValues,
    } = methods;

    const triggerValidation = (type: 'shipper' | 'consignee') => {
        if (isSubmitted) {
            if (type === 'shipper') {
                trigger('shipperCompanyName');
                trigger('shipperAddress');
            } else {
                trigger('consigneeCompanyName');
                trigger('consigneeAddress');
            }
        }
    };

    const onSubmit = (formData: any) => {
        onSaveNext(formData, QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS);
    };

    const handleBack = () => {
        const formData = getValues();
        onBack(formData, QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS);
    };

    const handleNext = useCallback(async () => {
        const isValid = await trigger();

        if (!isValid) {
            return false;
        }

        // Update Redux with the form data
        handleSubmit(onSubmit)();

        return true;
    }, [trigger, handleSubmit]);

    const handleSwap = () => {
        const currentShipperCompanyName = getValues('shipperCompanyName');
        const currentShipperAddress = getValues('shipperAddress');
        const currentConsigneeCompanyName = getValues('consigneeCompanyName');
        const currentConsigneeAddress = getValues('consigneeAddress');

        setValue('shipperId', null);
        setValue('consigneeId', null);
        setValue('shipperCompanyName', currentConsigneeCompanyName);
        setValue('shipperAddress', currentConsigneeAddress);
        setValue('consigneeCompanyName', currentShipperCompanyName);
        setValue('consigneeAddress', currentShipperAddress);
    };

    const watchShipperCompanyName = watch('shipperCompanyName');
    const watchShipperAddress = watch('shipperAddress');
    const watchShipperFullAddress = watch('shipperAddress')?.fullAddress;
    const watchShipperId = watch('shipperId');

    const watchConsigneeCompanyName = watch('consigneeCompanyName');
    const watchConsigneeAddress = watch('consigneeAddress');
    const watchConsigneeFullAddress = watch('consigneeAddress')?.fullAddress;
    const watchConsigneeId = watch('consigneeId');

    useEffect(() => {
        const isSwapDisabled = !watchShipperAddress?.fullAddress && !watchConsigneeAddress?.fullAddress;

        setHeaderChildren?.(<StepperAction handleNext={handleSubmit(handleNext)} handleBack={handleBack} handleSwapClick={handleSwap} isSwapButtonDisabled={isSwapDisabled} />);

        return () => {
            setHeaderChildren?.(null);
        };
    }, [watchShipperAddress, watchConsigneeAddress]);

    const resetForm = () => {
        setValue('shipperCompanyName', '');
        setValue('shipperAddress', null);
    };

    const resetConsigneeForm = () => {
        setValue('consigneeCompanyName', '');
        setValue('consigneeAddress', null);
    };

    useEffect(() => {
        if (watchShipperId) {
            const currentLatitude = getValues('shipperLatitude');

            if (watchShipperAddress && currentLatitude) {
                return;
            }

            const data = isShipper ? shippers.find((shipperData: any) => shipperData.id === watchShipperId) : consignees.find((consigneeData: any) => consigneeData.id === watchShipperId);

            if (data) {
                setValue('shipperCompanyName', data.companyName);
                setValue('shipperAddress', data);
            }
        } else {
            const currentLatitude = getValues('shipperLatitude');

            if (!currentLatitude && !watchShipperAddress) {
                resetForm();
            }
        }
    }, [shippers, consignees, watchShipperId, isShipper]);

    // Separate useEffect for consignee
    useEffect(() => {
        if (watchConsigneeId) {
            const currentLatitude = getValues('consigneeLatitude');

            if (watchConsigneeAddress && currentLatitude) {
                return;
            }

            const data = consignees.find((consigneeData: any) => consigneeData.id === watchConsigneeId);

            if (data) {
                setValue('consigneeCompanyName', data.companyName);
                setValue('consigneeAddress', data);
            }
        } else {
            const currentLatitude = getValues('consigneeLatitude');

            if (!currentLatitude && !watchConsigneeAddress) {
                resetConsigneeForm();
            }
        }
    }, [consignees, watchConsigneeId]);

    const handleSwitchShipper = () => {
        setValue('shipperId', null);
        resetForm();
    };

    const handleSwitchConsignee = () => {
        setValue('consigneeId', null);
        resetConsigneeForm();
    };

    const handleCardActionType = (value: string) => () => {
        if (value === 'edit_id') {
            setIsEditId(true);
        } else if (value === 'delete_id') {
            // Open delete confirmation modal for shipper/consignee
            setDeleteAction({
                mode: 'delete',
                shipper: isShipper,
                consignee: !isShipper,
                isContact: false,
                contactId: null,
            });
        } else if (value === 'switch_id') {
            handleSwitchShipper();
        }

        triggerValidation('shipper');
    };

    const handleConsigneeCardActionType = (value: string) => () => {
        if (value === 'edit_id') {
            setIsEditConsigneeId(true);
        } else if (value === 'delete_id') {
            // Open delete confirmation modal for consignee
            setDeleteAction({
                mode: 'delete',
                shipper: false,
                consignee: true,
                isContact: false,
                contactId: null,
            });
        } else if (value === 'switch_id') {
            handleSwitchConsignee();
        }

        triggerValidation('consignee');
    };

    const handleShipperChange = (onChange: any) => (value: any) => {
        if (value) {
            onChange(value.value);
            const data = isShipper ? shippers.find((shipperData: any) => shipperData.id === value.value) : consignees.find((consigneeData: any) => consigneeData.id === value.value);

            if (data) {
                setValue('shipperCompanyName', data.companyName);
                setValue('shipperAddress', data);

                triggerValidation('shipper');
            }
        } else {
            onChange(null);
            resetForm();
        }
    };

    const handleConsigneeChange = (onChange: any) => (value: any) => {
        if (value) {
            onChange(value.value);
            const data = consignees.find((consigneeData: any) => consigneeData.id === value.value);

            if (data) {
                setValue('consigneeCompanyName', data.companyName);
                setValue('consigneeAddress', data);

                triggerValidation('consignee');
            }
        } else {
            onChange(null);
            resetConsigneeForm();
        }
    };

    const handleCreateNew = () => {
        setIsEditId(true);
        setValue('shipperId', null);
        resetForm();
    };

    const handleCreateNewConsignee = () => {
        setIsEditConsigneeId(true);
        setValue('consigneeId', null);
        resetConsigneeForm();
    };

    const handleGoogleAddressSelect = (type: 'shipper' | 'consignee') => (addressData: any) => {
        const addressObject = {
            fullAddress: addressData?.fullAddress ?? '',
            address1: addressData?.address1 ?? '',
            city: addressData?.city ?? '',
            country: addressData?.country ?? '',
            countryCode: addressData?.countryCode ?? '',
            latitude: addressData?.latitude ?? '',
            longitude: addressData?.longitude ?? '',
            postal: addressData?.postal ?? '',
            state: addressData?.state ?? '',
            stateCode: addressData?.stateCode ?? '',
            streetNumber: addressData?.streetNumber ?? '',
            timezone: addressData?.timezone ?? '',
            additionalServiceIds: addressData?.additionalServiceIds ?? [],
        };

        if (type === 'shipper') {
            setValue('shipperId', null);
            setValue('shipperCompanyName', '');
            setValue('shipperAddress', addressObject);
            triggerValidation('shipper');
        } else {
            setValue('consigneeId', null);
            setValue('consigneeCompanyName', '');
            setValue('consigneeAddress', addressObject);
            triggerValidation('consignee');
        }
    };

    const handleEditShipper = (isEdit?: boolean) => () => {
        setIsEditId(isEdit ?? true);
    };

    const handleEditConsignee = (isEdit?: boolean) => () => {
        setIsEditConsigneeId(isEdit ?? true);
    };

    const handleSaveShipper = (data: any) => {
        setIsEditId(false);
        setValue('shipperId', data.id);
        setValue('shipperCompanyName', data.companyName);
        setValue('shipperAddress', data);

        if (data.address || data) {
            // handleAddress('shipper', data.address || data);
        }
    };

    const handleSaveConsignee = (data: any) => {
        setIsEditConsigneeId(false);
        setValue('consigneeId', data.id);
        setValue('consigneeCompanyName', data.companyName);
        setValue('consigneeAddress', data);

        if (data.address || data) {
            // handleAddress('consignee', data.address || data);
        }
    };

    const handleShipperConsigneeInputChange = (e: any, { action: inputAction }: any) => {
        if (inputAction === 'input-change' || inputAction === 'input-blur' || inputAction === 'input-clear') {
            if (isShipper) {
                handleSearchShipper(e);
            } else {
                handleSearchConsignee(e);
            }
        }
    };

    const handleConsigneeInputChange = (e: any, { action: inputAction }: any) => {
        if (inputAction === 'input-change' || inputAction === 'input-blur' || inputAction === 'input-clear') {
            handleSearchConsignee(e);
        }
    };

    const handleAutocompleteChange = (value: any) => {
        // Handle undefined/null values
        if (!value) {
            if (!watchShipperFullAddress) {
                setValue('shipperAddress', null);
            }

            return;
        }

        // Handle DOM blur events (when onBlur is attached to input element)
        if (value.type === 'blur' || (value.target && value.type)) {
            if (!watchShipperFullAddress) {
                setValue('shipperAddress', null);
            }

            return;
        }

        // Handle empty/cleared address (when input is cleared, an empty address object is passed)
        if (typeof value === 'object' && !value.target && (value.fullAddress === '' || (!value.fullAddress && !value.address1 && !value.city))) {
            setValue('shipperAddress', null);

            return;
        }

        // Handle address object from autocomplete selection
        // Ensure it's an address object, not a DOM event
        if (typeof value === 'object' && !value.target && value.fullAddress !== undefined) {
            setValue('shipperAddress', {
                ...(watchShipperAddress || {}),
                fullAddress: value.fullAddress ?? '',
                address1: value.address1 ?? watchShipperAddress?.address1 ?? '',
                city: value.city ?? watchShipperAddress?.city ?? '',
                country: value.country ?? watchShipperAddress?.country ?? '',
                countryCode: value.countryCode ?? watchShipperAddress?.countryCode ?? '',
                latitude: value.latitude ?? watchShipperAddress?.latitude ?? '',
                longitude: value.longitude ?? watchShipperAddress?.longitude ?? '',
                postal: value.postal ?? watchShipperAddress?.postal ?? '',
                state: value.state ?? watchShipperAddress?.state ?? '',
                stateCode: value.stateCode ?? watchShipperAddress?.stateCode ?? '',
                streetNumber: value.streetNumber ?? watchShipperAddress?.streetNumber ?? '',
                timezone: value.timezone ?? watchShipperAddress?.timezone ?? '',
                additionalServiceIds: value.additionalServiceIds ?? watchShipperAddress?.additionalServiceIds ?? [],
            });
        }
    };

    const handleConsigneeAutocompleteChange = (value: any) => {
        // Handle undefined/null values
        if (!value) {
            if (!watchConsigneeFullAddress) {
                setValue('consigneeAddress', null);
            }

            return;
        }

        // Handle DOM blur events (when onBlur is attached to input element)
        if (value.type === 'blur' || (value.target && value.type)) {
            if (!watchConsigneeFullAddress) {
                setValue('consigneeAddress', null);
            }

            return;
        }

        // Handle empty/cleared address (when input is cleared, an empty address object is passed)
        if (typeof value === 'object' && !value.target && (value.fullAddress === '' || (!value.fullAddress && !value.address1 && !value.city))) {
            setValue('consigneeAddress', null);

            return;
        }

        // Handle address object from autocomplete selection
        // Ensure it's an address object, not a DOM event
        if (typeof value === 'object' && !value.target && value.fullAddress !== undefined) {
            setValue('consigneeAddress', {
                ...(watchConsigneeAddress || {}),
                fullAddress: value.fullAddress ?? '',
                address1: value.address1 ?? watchConsigneeAddress?.address1 ?? '',
                city: value.city ?? watchConsigneeAddress?.city ?? '',
                country: value.country ?? watchConsigneeAddress?.country ?? '',
                countryCode: value.countryCode ?? watchConsigneeAddress?.countryCode ?? '',
                latitude: value.latitude ?? watchConsigneeAddress?.latitude ?? '',
                longitude: value.longitude ?? watchConsigneeAddress?.longitude ?? '',
                postal: value.postal ?? watchConsigneeAddress?.postal ?? '',
                state: value.state ?? watchConsigneeAddress?.state ?? '',
                stateCode: value.stateCode ?? watchConsigneeAddress?.stateCode ?? '',
                streetNumber: value.streetNumber ?? watchConsigneeAddress?.streetNumber ?? '',
                timezone: value.timezone ?? watchConsigneeAddress?.timezone ?? '',
                additionalServiceIds: value.additionalServiceIds ?? watchConsigneeAddress?.additionalServiceIds ?? [],
            });
        }
    };

    const handleModalClose = (closeStatus: boolean) => () => {
        // Reset action state
        setDeleteAction({
            mode: '',
            shipper: false,
            consignee: false,
            isContact: false,
            contactId: null,
        });

        if (closeStatus) {
            setIsDeleting(true);

            const idToDelete = deleteAction.shipper ? (watchShipperId ? +watchShipperId : null) : watchConsigneeId ? +watchConsigneeId : null;

            if (idToDelete) {
                deleteSingleAddress(idToDelete.toString())
                    .then((result: any) => {
                        if (result) {
                            if (deleteAction.shipper) {
                                setValue('shipperAddress', null);
                                setValue('shipperId', null);
                                setValue('shipperCompanyName', '');
                            } else {
                                setValue('consigneeAddress', null);
                                setValue('consigneeId', null);
                                setValue('consigneeCompanyName', '');
                            }

                            if (deleteAction.shipper) {
                                const updatedShippers = shippers.filter((shipper: any) => shipper.id !== idToDelete);
                                const updatedShipperOptions = shipperOptions.filter((option: any) => option.value !== idToDelete);
                                dispatch(setShippers(updatedShippers));
                                dispatch(setShipperOptions(updatedShipperOptions));
                            } else {
                                const updatedConsignees = consignees.filter((consignee: any) => consignee.id !== idToDelete);
                                const updatedConsigneeOptions = consigneeOptions.filter((option: any) => option.value !== idToDelete);
                                dispatch(setConsignees(updatedConsignees));
                                dispatch(setConsigneeOptions(updatedConsigneeOptions));
                            }

                            const updateData = deleteAction.shipper
                                ? {
                                      shipperConsigneeDetails: {
                                          ...shipperConsigneeDetails,
                                          shipperId: null,
                                          shipperCompanyName: '',
                                          shipperAddress: null,
                                      },
                                  }
                                : {
                                      shipperConsigneeDetails: {
                                          ...shipperConsigneeDetails,
                                          consigneeId: null,
                                          consigneeCompanyName: '',
                                          consigneeAddress: null,
                                      },
                                  };

                            dispatch(setCreateQuoteData({ data: updateData }));
                        }
                    })
                    .catch(async (error: any) => {
                        const errorMessage = error?.response?.data?.message || (deleteAction.shipper ? t('ValidationMessages.Shipper_delete_failed') : t('ValidationMessages.Consignee_delete_failed'));
                        WalToast.error(errorMessage);
                    })
                    .finally(() => {
                        setIsDeleting(false);
                    });
            } else {
                setIsDeleting(false);
            }
        }
    };

    return (
        <>
            <div className="space-y-5">
                {/* Shipper Section */}
                {!isEditId && (
                    <div className="stepper-content-container">
                        <div className="stepper-lable-wrap flex">
                            <label className="stepper-label-common">{isShipper ? t('QuotingHub.CreateQuote.Shipper_Details.Shipper') : t('QuotingHub.CreateQuote.Consignee_Details.Consignee')}</label>
                            <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                        </div>
                        <div className="flex-1 space-y-3">
                            <ShipperConsigneeAutocomplete
                                control={control}
                                isShipper={isShipper}
                                isEditId={isEditId}
                                watchCompanyName={watchShipperCompanyName}
                                watchShipperAddress={watchShipperAddress}
                                watchShipperFullAddress={watchShipperFullAddress}
                                shipperOptions={shipperOptions}
                                consigneeOptions={consigneeOptions}
                                errors={errors}
                                handleShipperChange={handleShipperChange}
                                handleCreateNew={handleCreateNew}
                                handleCardActionType={handleCardActionType}
                                onInputChange={handleShipperConsigneeInputChange}
                                fieldName="shipperId"
                                isQuoteCreateForm={true}
                                showDeleteOption={!!watchShipperId}
                                handleGoogleAddressSelect={handleGoogleAddressSelect('shipper')}
                                watchShipperConsigneeId={watchShipperId}
                            />

                            <div className="rounded-lg shadow-[0px_4px_8px_-2px_rgba(16,_24,_40,_0.10),_0px_2px_4px_-2px_rgba(16,_24,_40,_0.06)] overflow-hidden">
                                <MapBox
                                    draggable={false}
                                    address={watchShipperAddress && watchShipperAddress.fullAddress && watchShipperAddress.fullAddress !== '' ? watchShipperAddress : null}
                                    id="shipperMap"
                                    onReverseAddressFunc={handleAutocompleteChange}
                                    className="rounded-lg bg-white h-full border border-utilityGray200"
                                    isDefaultSatelliteView={true}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {isEditId && (
                    <ShipperConsigneeForm
                        handleEditShipper={handleEditShipper}
                        id={watchShipperId ? +watchShipperId : undefined}
                        handleSaveShipper={handleSaveShipper}
                        isShipper={isShipper}
                        isOpenFromQuoting={true}
                        defaultCompanyName={watchShipperCompanyName || ''}
                        defaultAddress={watchShipperAddress || null}
                    />
                )}
            </div>
            <div className="border-t border-utilityGray200 space-y-5 border-b pb-5 stepper-content-container-wrap">
                {/* Consignee Section */}
                {!isEditConsigneeId && (
                    <div className="stepper-content-container ">
                        <div className="stepper-lable-wrap flex">
                            <>
                                <label className="stepper-label-common">{t('QuotingHub.CreateQuote.Consignee_Details.Consignee')}</label>
                                <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                            </>
                        </div>
                        <div className="flex-1 space-y-3">
                            <ShipperConsigneeAutocomplete
                                control={control}
                                isShipper={false}
                                isEditId={isEditConsigneeId}
                                watchCompanyName={watchConsigneeCompanyName}
                                watchShipperAddress={watchConsigneeAddress}
                                watchShipperFullAddress={watchConsigneeFullAddress}
                                shipperOptions={shipperOptions}
                                consigneeOptions={consigneeOptions}
                                errors={errors}
                                handleShipperChange={handleConsigneeChange}
                                handleCreateNew={handleCreateNewConsignee}
                                handleCardActionType={handleConsigneeCardActionType}
                                onInputChange={handleConsigneeInputChange}
                                fieldName="consigneeId"
                                isQuoteCreateForm={true}
                                showDeleteOption={!!watchConsigneeId}
                                handleGoogleAddressSelect={handleGoogleAddressSelect('consignee')}
                                watchShipperConsigneeId={watchConsigneeId}
                            />
                            {!isEditConsigneeId && (
                                <div className="rounded-lg shadow-[0px_4px_8px_-2px_rgba(16,_24,_40,_0.10),_0px_2px_4px_-2px_rgba(16,_24,_40,_0.06)] overflow-hidden">
                                    <MapBox
                                        draggable={false}
                                        address={watchConsigneeAddress && watchConsigneeAddress.fullAddress && watchConsigneeAddress.fullAddress !== '' ? watchConsigneeAddress : null}
                                        id="consigneeMap"
                                        onReverseAddressFunc={handleConsigneeAutocompleteChange}
                                        className="rounded-lg bg-white h-full border border-utilityGray200"
                                        isDefaultSatelliteView={true}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {isEditConsigneeId && (
                    <ShipperConsigneeForm
                        handleEditShipper={handleEditConsignee}
                        id={watchConsigneeId ? +watchConsigneeId : undefined}
                        handleSaveShipper={handleSaveConsignee}
                        isShipper={false}
                        isOpenFromQuoting={true}
                        defaultCompanyName={watchConsigneeCompanyName || ''}
                        defaultAddress={watchConsigneeAddress || null}
                    />
                )}

                {deleteAction.mode === 'delete' && (
                    <DeleteModal
                        handleClose={handleModalClose}
                        moduleTitle={t('Orders.remove_from_wal_hq', {
                            companyName: deleteAction.shipper ? watchShipperCompanyName || '' : watchConsigneeCompanyName || '',
                        })}
                        moduleName={deleteAction.shipper ? t('Common.Shipper') : t('Common.Consignee')}
                        isDeleteLoading={isDeleting}
                    />
                )}
            </div>
        </>
    );
};

export default ShipperConsignee;
